/**
 * Cuentas cuyo consumo se desplomó contra el mes anterior.
 *
 * Es la señal de churn más temprana que tiene la empresa: una cuenta cancela
 * mucho después de dejar de usar, y la caída se ve semanas antes del aviso de
 * baja. Por eso va en la zona 1 y ordenada por magnitud, no por nombre.
 *
 * Tres decisiones de lectura:
 *
 *  - **El umbral se dice, no se supone.** `−30%` llega en el payload
 *    (`umbralCaidaPct`) y se imprime en el pie: sin eso, quien mira la tabla no
 *    sabe por qué esta cuenta está acá y aquella no.
 *  - **Cada porcentaje lleva su base.** `DeltaTag` imprime "contra junio 2026
 *    (325.020)" al lado del `−47,9%`, así el número se puede rehacer a mano.
 *  - **El sparkline no es el dato.** Los seis meses van en el gráfico *y* en el
 *    nombre accesible del gráfico, porque una línea de 64px no se lee con un
 *    lector de pantalla. Un hueco en la serie es un mes sin medición, nunca un
 *    cero.
 */
import type { Links, UsageClientRow } from '@ops/contracts'
import { CLIENT_STATE_LABELS, formatPeriodoCorto } from '@ops/contracts'

import {
  formatFechaCorta,
  formatMetricValue,
  formatPeriodo,
  formatRelativeDays,
  plural,
} from '../../lib/format'
import { rutaCliente } from '../../lib/rutas'
import {
  Button,
  DataTable,
  DeltaTag,
  MissingCell,
  Panel,
  ProvenanceBadge,
  Sparkline,
  StatusPill,
  type Column,
} from '../../ui'
import { Vacio } from '../comunes'
import { baseDeVariacion, severidadDeCaida } from './derivar'
import { Link } from 'react-router-dom'

export interface CuentasEnCaidaProps {
  filas: readonly UsageClientRow[]
  links: Links
  umbralCaidaPct: number
  onCrearSeguimiento: (fila: UsageClientRow) => void
  now: Date
  id?: string
}

/** La serie del sparkline, dicha en palabras para quien no ve el gráfico. */
function etiquetaSerie(fila: UsageClientRow): string {
  const puntos = fila.serie
    .map(
      (p) =>
        `${formatPeriodoCorto(p.periodo)} ${
          p.creditos === null ? 'sin medición' : formatMetricValue(p.creditos, 'count')
        }`,
    )
    .join(', ')
  return `Créditos de ${fila.clientNombre} mes a mes: ${puntos}`
}

export function CuentasEnCaida({
  filas,
  links,
  umbralCaidaPct,
  onCrearSeguimiento,
  now,
  id,
}: CuentasEnCaidaProps) {
  const columnas: ReadonlyArray<Column<UsageClientRow>> = [
    {
      key: 'cuenta',
      header: 'Cuenta',
      rowHeader: true,
      width: '220px',
      sortValue: (f) => f.clientNombre,
      filterValue: (f) => `${f.clientNombre} ${CLIENT_STATE_LABELS[f.estado]}`,
      render: (f) => (
        <span className="flex min-w-0 flex-col gap-0.5">
          {/* Adentro del panel, no afuera: la ficha de la cuenta es una pantalla
              propia y el link interno no lleva el aviso de pestaña nueva. */}
          <Link
            to={rutaCliente(f.clientId)}
            className="truncate font-semibold text-accent-text underline decoration-from-font underline-offset-2 hover:text-text"
          >
            {f.clientNombre}
          </Link>
          <StatusPill severidad="unknown" size="sm" icono={false}>
            {CLIENT_STATE_LABELS[f.estado]}
          </StatusPill>
        </span>
      ),
    },
    {
      key: 'variacion',
      header: 'Variación de créditos',
      width: '250px',
      sortValue: (f) => f.variacionCreditos?.pct ?? null,
      render: (f) =>
        f.variacionCreditos === null ? (
          <MissingCell que="mes anterior comparable" />
        ) : (
          <DeltaTag
            deltaPct={f.variacionCreditos.pct}
            base={baseDeVariacion(f.variacionCreditos)}
            unidad="count"
            direccion="higher_better"
            size="sm"
          />
        ),
    },
    {
      key: 'creditos',
      header: 'Créditos del período',
      align: 'right',
      width: '160px',
      sortValue: (f) => f.creditosConsumidos,
      render: (f) =>
        f.creditosConsumidos === null ? (
          <MissingCell que="medición" />
        ) : (
          formatMetricValue(f.creditosConsumidos, 'count')
        ),
    },
    {
      key: 'serie',
      header: 'Últimos 6 meses',
      width: '120px',
      render: (f) => (
        <Sparkline
          valores={f.serie.map((p) => p.creditos)}
          health={severidadDeCaida(f.variacionCreditos?.pct, umbralCaidaPct)}
          label={etiquetaSerie(f)}
        />
      ),
    },
    {
      key: 'contacto',
      header: 'Último contacto',
      width: '160px',
      sortValue: (f) => f.ultimoContacto,
      render: (f) =>
        f.ultimoContacto === null ? (
          /* Una cuenta que se desploma y que nadie llamó es la peor
             combinación de la tabla, y un guion la escondería. */
          <MissingCell que="contacto registrado" />
        ) : (
          <span className="flex flex-col gap-0.5">
            <span className="tabular">{formatFechaCorta(f.ultimoContacto)}</span>
            <span className="text-micro text-muted-strong">
              {formatRelativeDays(f.ultimoContacto, now)}
            </span>
          </span>
        ),
    },
    {
      key: 'fuente',
      header: 'Fuente',
      width: '180px',
      render: (f) => (
        <ProvenanceBadge
          provenance={f.provenance}
          now={now}
          variant="inline"
          hrefAdmin="/admin"
        />
      ),
    },
    {
      key: 'accion',
      header: 'Seguimiento',
      width: '150px',
      render: (f) => (
        <Button variante="chico" icono="mas" onClick={() => onCrearSeguimiento(f)}>
          Asignar seguimiento
          <span className="sr-only"> de {f.clientNombre}</span>
        </Button>
      ),
    },
  ]

  if (filas.length === 0) {
    return (
      <div id={id} className="min-w-0">
        <Panel titulo="Cuentas en caída de consumo" padding="sm">
          <Vacio
            icono={null}
            mensaje={`Ninguna cuenta cayó ${formatMetricValue(Math.abs(umbralCaidaPct), 'percent')} o más contra el mes anterior.`}
            detalle="Solo entran acá las cuentas con dos meses medidos: una cuenta sin mes anterior no se puede comparar y no aparece."
          />
        </Panel>
      </div>
    )
  }

  const peor = filas[0]

  return (
    <div id={id} className="min-w-0">
      <Panel
        titulo="Cuentas en caída de consumo"
        subtitulo={
          peor === undefined || peor.variacionCreditos === null ? (
            'Ordenadas por magnitud de la caída.'
          ) : (
            <>
              Ordenadas por magnitud. La peor es <b>{peor.clientNombre}</b>, con{' '}
              <b>{formatMetricValue(peor.variacionCreditos.pct, 'percent')}</b>{' '}
              contra {formatPeriodo(peor.variacionCreditos.base.periodo)} (
              {formatMetricValue(peor.variacionCreditos.base.valor, 'count')}{' '}
              créditos).
            </>
          )
        }
        severidad="critical"
        padding="none"
        footer={
          <>
            Entra acá toda cuenta con una caída de{' '}
            <b>{formatMetricValue(umbralCaidaPct, 'percent')}</b> o peor contra el
            mes anterior. Son{' '}
            <b>{formatMetricValue(filas.length, 'count')}</b>{' '}
            {plural(filas.length, 'cuenta', 'cuentas')} de las que tienen dos
            meses medidos.
          </>
        }
      >
        <DataTable
          columns={columnas}
          rows={filas}
          rowKey={(f) => f.clientId}
          rowSeverity={(f) =>
            severidadDeCaida(f.variacionCreditos?.pct, umbralCaidaPct)
          }
          densidadPorSeveridad
          filtroPlaceholder="cuenta o estado"
          emptyMessage="Ninguna cuenta cayó por debajo del umbral"
          caption={
            <>
              Cuentas cuyo consumo de créditos cayó{' '}
              {formatMetricValue(umbralCaidaPct, 'percent')} o más contra el mes
              anterior. Créditos y trazas salen de Langfuse; el último contacto,
              del CRM.
            </>
          }
        />
      </Panel>
    </div>
  )
}
